import isEqual from "lodash/isEqual";
import {
  getDurationText,
  getFormattedRangeDates,
  getRangePhaseText
} from "PFApp/components/readable_availability_rules/readable_availability_rules.utils";
import { Inline } from "PFComponents/containers";
import { getWeekdaysMin } from "PFCore/helpers/date";
import { roundToDecimals } from "PFCore/helpers/rounding";
import {
  AvailabilityMode,
  AvailabilityRange,
  AvailabilityRule,
  StandardRange,
  TimeRuleRange
} from "PFTypes/booking/availability";
import { Phase } from "PFTypes/phase";
import { TFunction } from "react-i18next";

import { AvailabilityChangelog, ChangelogElement } from "./use_availability_changelog.types";

const isTimeRuleRange = (range: AvailabilityRange): range is TimeRuleRange => "dayOfWeek" in range;

const simplifyRanges = (before: AvailabilityRange[], after: AvailabilityRange[]) => {
  const beforeIndexesToRemove = new Set<number>();
  const afterIndexesToRemove = new Set<number>();

  before.forEach((beforeRange, beforeIndex) => {
    const afterIndex = after.findIndex(
      (afterRange, index) => !afterIndexesToRemove.has(index) && isEqual(beforeRange, afterRange)
    );
    if (afterIndex !== -1) {
      beforeIndexesToRemove.add(beforeIndex);
      afterIndexesToRemove.add(afterIndex);
    }
  });

  return {
    before: before.filter((_, index) => !beforeIndexesToRemove.has(index)),
    after: after.filter((_, index) => !afterIndexesToRemove.has(index))
  };
};

type BuildRangeGroupParams = {
  range: AvailabilityRange;
  counterpart?: AvailabilityRange;
  modeChanged: boolean;
  mode: AvailabilityMode;
  phases: Phase[];
  t: TFunction<["activities", "translation", "core"]>;
  formatDate: (date: string) => string;
  isAfterSide: boolean;
  pill?: "added" | "removed";
};

const buildRangeGroup = ({
  range,
  counterpart,
  modeChanged,
  mode,
  phases,
  t,
  formatDate,
  isAfterSide,
  pill
}: BuildRangeGroupParams): ChangelogElement[] => {
  const elements: ChangelogElement[] = [];

  const datesChanged = !!counterpart && (range.start !== counterpart.start || range.end !== counterpart.end);
  elements.push({
    content: getFormattedRangeDates(range, formatDate),
    highlighted: isAfterSide && datesChanged,
    separator: true
  });

  const hasPhase = !!range.phaseSourceId;

  if (isTimeRuleRange(range)) {
    const other = counterpart && isTimeRuleRange(counterpart) ? counterpart : undefined;
    const weekdaysMin = getWeekdaysMin();
    range.dayOfWeek!.forEach((minutes, index) => {
      const highlighted = !!counterpart && (modeChanged || other?.dayOfWeek![index] !== minutes);
      elements.push({
        content: (
          <Inline gap={null}>
            {weekdaysMin[index]}
            <strong>{roundToDecimals(minutes / 60)}</strong>
          </Inline>
        ),
        highlighted: isAfterSide && highlighted,
        separator: hasPhase && index === range.dayOfWeek.length - 1
      });
    });
  } else {
    const other = counterpart && !isTimeRuleRange(counterpart) ? (counterpart as StandardRange) : undefined;
    const durationChanged = !!counterpart && (modeChanged || !isEqual(range.duration, other?.duration));
    elements.push({
      content: getDurationText(range.duration, mode, t),
      highlighted: isAfterSide && durationChanged,
      separator: hasPhase
    });
  }

  if (hasPhase) {
    const phaseText = getRangePhaseText(phases, range) ?? range.phaseSourceId ?? "";
    const isChanged = !!counterpart && counterpart.phaseSourceId !== range.phaseSourceId;
    elements.push({ content: phaseText, highlighted: isAfterSide && isChanged });
  }

  if (pill) {
    elements.push({ pill });
  }

  return elements;
};

export const getAvailabilityChangelog = (
  before: AvailabilityRule | null,
  after: AvailabilityRule | null,
  t: TFunction<["activities", "translation", "core"]>,
  formatDate: (date: string) => string,
  phases: Phase[] = []
): AvailabilityChangelog => {
  const modeChanged = !!before && !!after && before.mode !== after.mode;

  const { before: beforeRanges, after: afterRanges } = modeChanged
    ? { before: before!.ranges, after: after!.ranges }
    : simplifyRanges(before?.ranges ?? [], after?.ranges ?? []);

  const beforeGroups: ChangelogElement[][] = [];
  const afterGroups: ChangelogElement[][] = [];

  if (before && after && before.availabilityThreshold !== after.availabilityThreshold) {
    const thresholdLabel = `${t("activities:notificationChangelog.threshold")}:`;
    beforeGroups.push([{ content: thresholdLabel }, { content: `${before.availabilityThreshold}%` }]);
    afterGroups.push([
      { content: thresholdLabel },
      { content: `${after.availabilityThreshold}%`, highlighted: true }
    ]);
  }

  const rangesCount = Math.max(beforeRanges.length, afterRanges.length);
  const commonBuilderProps = { modeChanged, phases, t, formatDate };

  for (let index = 0; index < rangesCount; index++) {
    const beforeRange = beforeRanges[index];
    const afterRange = afterRanges[index];

    if (beforeRange && afterRange) {
      beforeGroups.push(
        buildRangeGroup({
          ...commonBuilderProps,
          range: beforeRange,
          counterpart: afterRange,
          mode: before!.mode,
          isAfterSide: false
        })
      );
      afterGroups.push(
        buildRangeGroup({
          ...commonBuilderProps,
          range: afterRange,
          counterpart: beforeRange,
          mode: after!.mode,
          isAfterSide: true
        })
      );
    } else if (afterRange) {
      afterGroups.push(
        buildRangeGroup({
          ...commonBuilderProps,
          range: afterRange,
          mode: after!.mode,
          isAfterSide: true,
          pill: "added"
        })
      );
    } else if (beforeRange) {
      beforeGroups.push(
        buildRangeGroup({
          ...commonBuilderProps,
          range: beforeRange,
          mode: before!.mode,
          isAfterSide: false,
          pill: "removed"
        })
      );
    }
  }

  return { before: beforeGroups, after: afterGroups };
};
